import Phones from './../collections/Phones';
import Inbox from '../models/Inbox';
import sorting from '../../utils/sorting';
import Phone from 'store/models/Phone';
import { baseApi } from 'core/api';
import { useInboxesStore, useOrganizationStore } from 'store/modules';

const state = () => ({
    _number: null,
    _numbers: [],
    _numbersLoading: false,
    _numbersPagination: {
        hasNextPage: false,
        currentPage: 1,
    },
    _unassignedNumbers: [],
    _unassignedNumbersPagination: {
        hasNextPage: false,
        currentPage: 1,
    },
    _numbersSearch: '',
    _numberVendor: {
        key: '',
        isBillable: false,
    },
    _notificationIncomplete: true,
    _parameters: {
        sortOrder: null,
        sortBy: null,
    },
    _unassignedSort: {
        sortOrder: null,
        sortBy: null,
    },
    _refreshLoading: false,
    _numberVendorIntegrations: null,
});

const getters = {
    number: (state) => state._number,
    numbers: (state) => state._numbers,
    numbersLoading: (state) => state._numbersLoading,
    numbersPagination: (state) => state._numbersPagination,
    unassignedNumbers: (state) => state._unassignedNumbers,
    unassignedNumbersPagination: (state) => state._unassignedNumbersPagination,
    unassignedActiveFieldsColumn: (state) => {
        if (!state._unassignedNumbers_fields) {
            return ['number', 'action'];
        }

        return !state._unassignedNumbers_fields.active_fields;
    },
    unassignedNumbersParameters: (state) => state._unassignedNumbersParameters,
    numberVendor: (state) => state._numberVendor,
    hasNotificationIncomplete: (state) => state._notificationIncomplete,
    hasNumber: (state) => state._number && state._number.id !== 0,
    numberUser: (state) => state._number,
    numbersParameters: (state) => state._parameters,
    refreshLoading: (state) => state._refreshLoading,
    numberVendorIntegrations: (state) => state._numberVendorIntegrations,
};

const actions = {
    clearNotificationIncomplete() {
        this._setNotificationIncomplateNumbers();
    },

    deleteNumbersByIdRelease({ id, form, isRequest = true }) {
        const request = form || baseApi;

        if (!isRequest) {
            this._setDeleteNumbers(id);

            return;
        }

        return request.delete(`core/numbers/${id}/release`).then((response) => {
            this._setDeleteNumbers(id);

            return response;
        });
    },

    updateCallForwarding(number) {
        this._setUpdateCallForwardingNumbers(number);
    },

    resetNumbers() {
        this._setResetNumbers();
    },

    getNumbers({ query = '', pagination, search = false, numbersType = '' } = {}) {
        if (!pagination) {
            pagination = this._numbersPagination;
        }

        let page = pagination.currentPage;
        const params = {
            page,
            limit: 500,
            sortBy: this._parameters.sortBy,
            sortOrder: this._parameters.sortOrder,
        };

        if (search) {
            params.page = 1;
        } else {
            if (pagination.hasNextPage) {
                page++;
            }

            params.page = page;
        }

        if (query) {
            params.query = query;
        }

        if (numbersType) {
            params.numbersType = numbersType;
        }

        this._setFetchNumbersLoading(true);

        return baseApi
            .get('core/numbers', {
                params: params,
            })
            .then((response) => {
                this._setFetchNumbers({
                    ...response.data,
                    search,
                });
                this._setFetchNumbersLoading(false);

                return response;
            });
    },

    getNumbersUnassigned({
        query = '',
        pagination,
        search = false,
        limit = 10,
        queryParams = {},
    } = {}) {
        if (!pagination) {
            pagination = this._unassignedNumbersPagination;
        }

        let page = pagination.currentPage;
        const params = {
            page,
            limit,
            ...this._unassignedSort,
            ...queryParams,
        };

        if (search) {
            params.page = 1;
        } else {
            if (pagination.hasNextPage) {
                page++;
            }

            params.page = page;
        }

        if (query) {
            params.query = query;
        }

        return baseApi.get('core/numbers/unassigned', { params: params }).then((response) => {
            this._setFetchUnassignedNumbers({
                ...response.data,
                search,
            });

            return response;
        });
    },

    numbersClearSearch() {},
    unassignedNumbersClearSearch() {},

    updateNumbersSearch(form) {
        return form.post('core/numbers/search');
    },

    getNumbersGetFromPool(params) {
        return baseApi.get('core/numbers/get-from-pool', {
            params: params,
        });
    },

    updateNumbers(form) {
        return form.post('core/numbers').then((response) => {
            this._setNumberSelected(response.data);

            return response;
        });
    },

    updateNumbersPft(pftNumber) {
        return baseApi
            .post('core/numbers/pft', {
                pft_number: pftNumber,
            })
            .then((response) => {
                return response;
            });
    },

    getNumbersLookup(number) {
        return baseApi.get(`core/numbers/lookup?number=${number}`);
    },

    getNumberZipwhipFetch({ username, password }) {
        return baseApi.post(`core/zipwhip/token?username=${username}&password=${password}`);
    },

    createNumbersZipwhipCreate(form) {
        return form.post('core/numbers/zipwhip/create');
    },

    startZipWhipMigration(numberId) {
        return baseApi.post(`core/numbers/zipwhip/${numberId}/migration`);
    },

    getNumbersLandLineFetch({ number, cancelToken }) {
        const config = {};

        if (cancelToken) {
            config.cancelToken = cancelToken;
        }

        return baseApi
            .get(`core/numbers/landline/fetch?phone=${number}`, config)
            .then((response) => {
                return response.data;
            });
    },

    createNumbersLandLineCreate(form) {
        return form.post('core/numbers/landline/create');
    },

    deleteNumbersLandLineDelete({ number }) {
        return baseApi.delete(`core/numbers/landline/delete?phone=${number}`).then((response) => {
            return response.data;
        });
    },

    updateNumbersLandlineVerifyRequest(form) {
        return form.post('core/numbers/landline/verify/request');
    },

    updateNumbersLandlineDocumentCreate(form) {
        return form.post('core/numbers/landline/document/create');
    },

    updateNumbersLandlineTeamCreate(form) {
        return form.post('core/numbers/zipwhip/team/create').then((response) => {
            useInboxesStore()._setInboxCreated(response.data);

            return new Inbox(response.data);
        });
    },

    updateNumbersLandlineInboxUpdate(form) {
        return form.post('core/numbers/landline/inbox/update');
    },

    updateNumbersAssignInboxById({ form, inboxId }) {
        return form.post(`core/numbers/assign-inbox/${inboxId}`).then((response) => {
            return response.data;
        });
    },

    updateNumbersByIdAssignInboxById({ form, inboxId, numberId }) {
        return form.post(`core/numbers/${numberId}/assign-inbox/${inboxId}`).then((response) => {
            return response.data;
        });
    },

    updateNumbersByIdUnassignFromInbox({ form, numberId }) {
        return form.post(`core/numbers/${numberId}/unassign-from-inbox`).then((response) => {
            return response.data;
        });
    },

    getNumbersVendors() {
        return baseApi.get('core/numbers/vendors').then((response) => {
            return response;
        });
    },

    getNumbersIntegration() {
        return baseApi.get('core/numbers/vendors/integration').then((response) => {
            this._setNumberVendorIntegrations(response.data);

            return response;
        });
    },

    updateNumbersByIdCallForwarding({ form, numberId }) {
        return form.post(`core/numbers/${numberId}/call_forwarding`).then((response) => {
            return response;
        });
    },

    getNumbersCallForwardingPreview(message) {
        const options = {
            params: {
                message: message,
            },
            responseType: 'blob',
        };

        return baseApi.get('core/numbers/call-forwarding/preview', options).then((response) => {
            return response.data;
        });
    },

    numbersUpdateSort({ sortBy, sortOrder }) {
        let numbers = sorting(this._numbers, { sortBy, sortOrder });

        this._setUpdateSortNumbers({ sortOrder, sortBy, numbers });
    },

    unassignedUpdateSort({ sortBy, sortOrder }) {
        let unassignedNumbers = sorting(this._unassignedNumbers, { sortBy, sortOrder });

        this._setUpdateSortUnassigned({ sortOrder, sortBy, unassignedNumbers });
    },

    refreshUnassignedNumbers() {
        this._setUpdateRefreshNumbersState(true);

        return baseApi.post('core/numbers/unassigned/refresh').finally(() => {
            this._setUpdateRefreshNumbersState(false);
        });
    },

    getOrganizationNumber() {
        return baseApi.get('core/organization/number').then((response) => {
            this._setOrganizationNumberLoaded(response.data);

            return response.data;
        });
    },

    _setOrganization(organization) {
        this._numberVendor.key = organization.number_vendor.key;
        this._numberVendor.isBillable = organization.number_vendor.isBillable;
    },

    _setNotificationIncomplateNumbers() {
        this._notificationIncomplete = false;
    },

    _setDeleteNumbers(id) {
        this._numbers = this._numbers.filter((item) => item.id !== id);
        this._unassignedNumbers = this._unassignedNumbers.filter((item) => item.id !== id);
    },

    _setUpdateCallForwardingNumbers(number) {
        let numbers = new Phones(this._numbers);

        this._numbers = numbers.updateCallForwarding(number);
    },

    _setResetNumbers() {
        let numbers = new Phones([]);

        this._numbers = numbers.models;
        this._numbersPagination = {
            hasNextPage: false,
            currentPage: 1,
        };
    },

    _setFetchNumbers({ data, next_page_url, current_page, search }) {
        let numbers = new Phones(data);

        this._numbers = numbers.models;

        this._numbersPagination = {
            hasNextPage: Boolean(next_page_url),
            currentPage: current_page,
        };
    },

    _setFetchNumbersLoading(status) {
        this._numbersLoading = status;
    },

    _setFetchUnassignedNumbers(data) {
        let numbers = new Phones(data.data);
        this._unassignedNumbers = numbers.models;

        this._unassignedNumbersPagination = {
            hasNextPage: Boolean(data.next_page_url),
            currentPage: data.current_page,
        };
    },

    _setOrganizationNumberLoaded(number) {
        this._number = new Phone(number);
    },

    _setNumberVendorIntegrations(numberVendorIntegrations) {
        this._numberVendorIntegrations = numberVendorIntegrations;
    },

    _setNumberSelected(number) {
        this._number = new Phone(number);
        useOrganizationStore()._setNumberSelected();
    },

    _setUpdateSortNumbers({ sortOrder, sortBy, numbers }) {
        this._parameters.sortBy = sortBy;
        this._parameters.sortOrder = sortOrder;

        this._numbers = numbers;
    },

    _setUpdateSortUnassigned({ sortOrder, sortBy, unassignedNumbers }) {
        this._unassignedSort.sortBy = sortBy;
        this._unassignedSort.sortOrder = sortOrder;

        this._unassignedNumbers = unassignedNumbers;
    },

    _setUpdateRefreshNumbersState(status) {
        this._refreshLoading = status;
    },
};

export default {
    state,
    getters,
    actions,
};
